// Page animations. initAnimations(scope, { delay }) builds everything for the
// page inside `scope` in one gsap.context, so a barba transition can revert
// it cleanly (ScrollTriggers, SplitText, inline styles) before the next page
// mounts. `delay` postpones the above-the-fold intro (the page wipe lifts first).
import { gsap, ScrollTrigger, SplitText, reducedMotion } from "./motion.js";

const root = document.documentElement;
const fine = () => window.matchMedia("(hover: hover) and (pointer: fine)").matches;

export function initAnimations(scope, { delay = 0.1 } = {}) {
  root.classList.add("anim-ready");
  if (reducedMotion()) return null;

  const q = (sel) => Array.from(scope.querySelectorAll(sel));
  const has = (sel) => scope.querySelector(sel) !== null;

  const ctx = gsap.context(() => {
    // ─── Above the fold ───────────────────────────────────────────────────
    const intro = gsap.timeline({ delay, defaults: { ease: "power3.out" } });

    if (has(".hero__card")) {
      intro
        .from(".hero__card", { y: 60, scale: 0.965, autoAlpha: 0, duration: 1.1, ease: "power4.out" })
        .from(".hero__eyebrow, .hero__tags .pill", { y: 18, autoAlpha: 0, duration: 0.7, stagger: 0.07 }, 0.35)
        .from(".hero__scallop", { scale: 0.2, rotate: -140, autoAlpha: 0, duration: 1.3, ease: "back.out(1.5)" }, 0.45)
        .from(".hero__lede, .hero__cta .btn", { y: 26, autoAlpha: 0, duration: 0.8, stagger: 0.1 }, 0.95);

      const heading = scope.querySelector(".hero__heading");
      if (heading) {
        SplitText.create(heading, {
          type: "lines",
          mask: "lines",
          maskClass: "split-mask",
          autoSplit: true,
          onSplit: (self) => gsap.from(self.lines, { yPercent: 115, duration: 1.1, stagger: 0.12, ease: "power4.out", delay: delay + 0.3 }),
        });
      }

      // The compass badge keeps slowly turning.
      gsap.to(".hero__scallop > svg", { rotate: 360, duration: 48, repeat: -1, ease: "none", transformOrigin: "50% 50%" });
    }

    if (has(".page-head")) {
      intro.from(".page-head > *", { y: 34, autoAlpha: 0, duration: 0.85, stagger: 0.1 }, 0.1);
    }

    if (has(".article")) {
      intro
        .from(".back-link, .article__cat", { y: 20, autoAlpha: 0, duration: 0.6, stagger: 0.08 }, 0)
        .from(".article__dek, .byline, .art-actions, .share", { y: 26, autoAlpha: 0, duration: 0.7, stagger: 0.09 }, 0.4)
        .fromTo(".article__media", { clipPath: "inset(0 0 100% 0 round 18px)" }, { clipPath: "inset(0 0 0% 0 round 18px)", duration: 1.2, ease: "power4.inOut" }, 0.55);
      const title = scope.querySelector(".article h1");
      if (title) {
        SplitText.create(title, {
          type: "lines",
          mask: "lines",
          maskClass: "split-mask",
          autoSplit: true,
          onSplit: (self) => gsap.from(self.lines, { yPercent: 110, duration: 1, stagger: 0.1, ease: "power4.out", delay: delay + 0.1 }),
        });
      }
      gsap.fromTo(".article__media img, .article__media svg", { scale: 1.25 }, { scale: 1, duration: 1.6, ease: "power3.out", delay: delay + 0.4 });
      // Slow parallax on the cover photo while scrolling past it.
      gsap.to(".article__media img", { yPercent: 10, ease: "none", scrollTrigger: { trigger: ".article__media", start: "top bottom", end: "bottom top", scrub: true } });
    }

    if (has(".notfound")) {
      intro
        .from(".notfound__big", { scale: 0.6, autoAlpha: 0, duration: 1, ease: "back.out(1.6)" })
        .from(".notfound h1, .notfound p, .notfound .btn", { y: 24, autoAlpha: 0, duration: 0.7, stagger: 0.1 }, 0.35);
    }

    // ─── Scroll reveals ───────────────────────────────────────────────────
    const reveal = (sel, vars = {}, start = "top 88%") =>
      q(sel).forEach((el) =>
        gsap.from(el, { y: 36, autoAlpha: 0, duration: 0.9, ease: "power3.out", ...vars, scrollTrigger: { trigger: el, start, once: true } })
      );

    reveal(".map-head > div, .map-head__actions, .sec-head, .brief__head, .quiz__head > *, .team .sec-head, .page .entry-content");

    // Map: the frame rises, then the pins pop in one by one.
    if (has(".map-shell")) {
      gsap.from(".map-shell", { y: 70, autoAlpha: 0, scale: 0.97, duration: 1.1, ease: "power3.out", scrollTrigger: { trigger: ".map-shell", start: "top 90%", once: true } });
      gsap.from(".pin__teardrop, .pin__bead", {
        scale: 0,
        svgOrigin: "0 1",
        autoAlpha: 0,
        duration: 0.6,
        stagger: { each: 0.025, from: "random" },
        ease: "back.out(2.2)",
        scrollTrigger: { trigger: ".map-shell", start: "top 70%", once: true },
      });
      gsap.from(".route-dash, .route-solid, .map-region", { autoAlpha: 0, duration: 1.4, stagger: 0.08, ease: "power2.out", scrollTrigger: { trigger: ".map-shell", start: "top 70%", once: true } });
    }

    if (has(".advice__card")) {
      gsap.from(".advice__card", { y: 60, scale: 0.97, autoAlpha: 0, duration: 1, ease: "power3.out", scrollTrigger: { trigger: ".advice__card", start: "top 88%", once: true } });
      gsap.from(".advice__lead > *, .advice__body > *", { y: 24, autoAlpha: 0, duration: 0.8, stagger: 0.08, delay: 0.25, ease: "power3.out", scrollTrigger: { trigger: ".advice__card", start: "top 80%", once: true } });
    }

    reveal(".quiz__card, .newsletter__card, .signup, .trip-cta", { y: 56, scale: 0.97, duration: 1 }, "top 90%");

    // Cards and boards arrive in a staggered wave per row.
    ["#stories .story, .story-grid--archive .story", ".related__grid .story", ".board", ".team-grid .member", ".mission .wp-block-column"].forEach((sel) => {
      if (!has(sel.split(",")[0].trim())) return;
      ScrollTrigger.batch(q(sel), {
        start: "top 90%",
        once: true,
        onEnter: (els) => gsap.fromTo(els, { y: 50, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.85, stagger: 0.1, ease: "power3.out", overwrite: true }),
      });
      gsap.set(q(sel), { autoAlpha: 0 });
    });

    q(".avatar").forEach((el) => gsap.from(el, { scale: 0.5, autoAlpha: 0, duration: 0.7, ease: "back.out(1.8)", scrollTrigger: { trigger: el, start: "top 92%", once: true } }));
    q(".loop li").forEach((el, i) => gsap.from(el, { x: 24, autoAlpha: 0, duration: 0.7, delay: i * 0.08, ease: "power3.out", scrollTrigger: { trigger: ".loop", start: "top 85%", once: true } }));

    if (has(".article__body")) {
      ScrollTrigger.batch(q(".article__body > *, .related h2"), {
        start: "top 92%",
        once: true,
        onEnter: (els) => gsap.fromTo(els, { y: 28, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.8, stagger: 0.08, ease: "power3.out", overwrite: true }),
      });
      gsap.set(q(".article__body > *, .related h2"), { autoAlpha: 0 });
    }

    q(".legal__toc a").forEach((el, i) => gsap.from(el, { x: -16, autoAlpha: 0, duration: 0.5, delay: 0.3 + i * 0.03, ease: "power2.out" }));

    // ─── Magnetic buttons (fine pointers only) ────────────────────────────
    if (fine()) {
      q(".hero__cta .btn, .newsletter__submit").forEach((el) => {
        const x = gsap.quickTo(el, "x", { duration: 0.5, ease: "power3.out" });
        const y = gsap.quickTo(el, "y", { duration: 0.5, ease: "power3.out" });
        el.addEventListener("pointermove", (e) => {
          const r = el.getBoundingClientRect();
          x((e.clientX - (r.left + r.width / 2)) * 0.28);
          y((e.clientY - (r.top + r.height / 2)) * 0.28);
        });
        el.addEventListener("pointerleave", () => {
          x(0);
          y(0);
        });
      });
    }
  }, scope);

  // Late layout changes (fonts, lazy images) must re-measure triggers.
  requestAnimationFrame(() => ScrollTrigger.refresh());
  return ctx;
}

// Footer lives outside barba's container, so it animates once.
export function initFooterAnimation() {
  if (reducedMotion()) return;
  const footer = document.querySelector(".footer");
  if (!footer) return;
  gsap.from(".footer__grid > *, .footer__bar", {
    y: 30,
    autoAlpha: 0,
    duration: 0.8,
    stagger: 0.08,
    ease: "power3.out",
    scrollTrigger: { trigger: footer, start: "top 92%", once: true },
  });
}
